import React, { useEffect } from "react";

//spinner
import { BounceLoader } from "react-spinners";

//redux
import { useSelector, useDispatch } from "react-redux";
import { CLOSE_LOADER } from "../store/Loader/loader.type";

/**
 * Global activity indicator.
 *
 * This used to be a MUI `Dialog`: a full-screen backdrop with
 * `disableEscapeKeyDown`. `state.loader.loader` is a plain boolean, so a single
 * request that opened it and never closed it — an unbalanced dispatch, a call
 * abandoned by a navigation, a stalled response — greyed out the whole panel
 * permanently with no way out. The page rendered behind it but every click
 * landed on the backdrop, which is exactly the "stuck after login" on the
 * content-creator KYC page.
 *
 * It is now a **non-blocking** overlay: no backdrop, `pointerEvents: none`, so
 * it floats above the page and the page underneath stays fully usable no matter
 * what the loader state is. A stuck spinner is now cosmetic instead of fatal.
 *
 * ⚠️ Trade-off accepted: this no longer prevents double-clicking a submit
 * button during a mutation. Controls that must not be pressed twice have to
 * disable themselves — relying on a modal to enforce that was always fragile,
 * since it depended on the spinner never getting out of sync.
 */

/**
 * Backstop: how long the spinner may stay up before it clears itself.
 *
 * Requests carry their own 30s ceiling (util/api.js), so reaching this means
 * something went wrong that we did not anticipate. Above that ceiling so a
 * slow-but-working request is never cut short, and high enough to cover a large
 * KYC upload, which is deliberately exempt from the request timeout.
 */
const LOADER_WATCHDOG_MS = 60000;

const Loader = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.loader.loader);

  useEffect(() => {
    if (!open) return undefined;

    const timer = setTimeout(() => {
      console.warn(
        `[loader] still open after ${LOADER_WATCHDOG_MS}ms — clearing it. Something opened the spinner without closing it.`
      );
      dispatch({ type: CLOSE_LOADER });
    }, LOADER_WATCHDOG_MS);

    return () => clearTimeout(timer);
  }, [open, dispatch]);

  if (!open) return null;

  return (
    <div
      // Fixed and centred, but transparent to both clicks and hit-testing, so
      // nothing underneath is ever blocked.
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: 2000,
      }}
      aria-hidden="true"
    >
      <div
        style={{
          background: "rgba(255,255,255,0.9)",
          borderRadius: "50%",
          padding: 14,
          boxShadow: "0 4px 16px rgba(16,24,40,0.18)",
        }}
      >
        <BounceLoader size={48} color="#222e3c" loading />
      </div>
    </div>
  );
};

export default Loader;
